import { Rgb } from './Rgb.js';
import type { AssertIsStringArray, Tuple } from '../types/index.js';

export type Label<T extends string = string> = {
  text: T;
  textColor: Rgb;
  backgroundColor: Rgb;
};

export namespace Label {
  export const BASE = {
    text: 'advt',
    textColor: Rgb.white,
    backgroundColor: Rgb.blue
  } as const satisfies Label;

  export type Mapper<TLabels extends ReadonlyArray<string>> = {
    [TIndex in keyof TLabels]: Label<TLabels[TIndex]>;
  };

  export namespace Sublabel {
    export type Mapper<
      TLabels extends ReadonlyArray<string>,
      TLabel extends TLabels[number],
      TSublabel extends string
    > = AssertIsStringArray<
      Tuple.Flatten<{
        [K in keyof TLabels]: TLabels[K] extends TLabel
          ? [TLabels[K], TSublabel]
          : [TLabels[K]];
      }>
    >;

    export function* produce<
      const TLabels extends ReadonlyArray<string>,
      const TLabel extends TLabels[number],
      const TSublabel extends string
    >(
      labels: Label.Mapper<TLabels>,
      labelText: TLabel,
      sublabelText: TSublabel
    ): Generator<
      Label.Mapper<Label.Sublabel.Mapper<TLabels, TLabel, TSublabel>>[number]
    > {
      for (const label of labels) {
        yield label;

        if (label.text !== labelText) continue;

        yield {
          backgroundColor: Rgb.Variant.make(label.backgroundColor),
          textColor: Rgb.Variant.make(label.textColor),
          text: sublabelText
        };
      }
    }
  }
}
